import { createSelector, createSlice } from '@reduxjs/toolkit'
import { createApiAsyncThunk } from '@/services/apiService'
import { setTimeoutInDev } from '@/utils/environment'
import { addFlashMessage } from '../flash/flashMessagesSlice'
import { sanitizeFormData, transformToFormData, hasFiles } from '@/utils/formData'
import { addToPrestataireItems } from '../prestataires/prestatairesSlice'

export const getFacturationEntiteValues = (item) => {
  // Option pour la compagnie HPI
  const options = { 
    'Company:direct': "HPI en direct",
    'Company:pour_compte_proprietaire': "HPI pour le compte du propriétaire"
  }

  if(item.entite_facturable?.match("Proprietaire:")) {
    options[item.entite_facturable] = "le propriétaire"
  }

  if(item.entite_facturable?.match("Locataire:")) {
    options[item.entite_facturable] = "le locataire"
  }

  return options
}

export const facturationEntiteOptions = (bien) => {
  // Option pour la compagnie HPI
  const options = [
    { label: "HAUT POTENTIEL IMMOBILIER (en direct)", value: 'Company:direct' },
    { label: "HPI pour le compte du propriétaire", value: 'Company:pour_compte_proprietaire' }
  ]

  if (!bien || (!bien.proprietaires && !bien.current_location?.locataires) ) {
    return options
  }

  // Ajouter une option pour chaque propriétaire du bien
  if (bien.proprietaires && bien.proprietaires.length > 0) {
    bien.proprietaires.forEach(proprietaire => {
      options.push({
        label: `Propriétaire: ${proprietaire.nom_complet || proprietaire.nom}`,
        value: `Proprietaire:${proprietaire.proprietaire_id}`
      })
    })
  }

  const locataires = bien.current_location?.locataires || []

  // Ajouter une option pour chaque locataire du bien
  if (locataires && locataires.length > 0) {
    locataires.forEach( locataire => {
      options.push({
        label: `Locataire: ${locataire.nom_complet || locataire.nom}`,
        value: `Locataire:${locataire.locataire_id}`
      })
    })
  }

  return options
}

// Fonction pour déterminer le mode_facturation_company selon l'entité facturable
export const getModeFacturationCompany = (entiteFacturable) => {
  if (!entiteFacturable || typeof entiteFacturable !== 'string') {
    return null
  }
  
  const [type, mode] = entiteFacturable.split(':')
  
  // Si c'est une Company, retourner le mode spécifié
  if (type === 'Company') {
    return mode || 'pour_compte_proprietaire' // valeur par défaut
  }
  
  // Si c'est un Proprietaire, pas de mode_facturation_company
  return null
}

// Fonction pour reconstituer la valeur entite_facturable depuis les données du backend
export const buildEntiteFacturableValue = (intervention) => {
  if (!intervention.entite_facturable_type) {
    return ''
  }
  
  const type = intervention.entite_facturable_type
  
  if (type === 'Company') {
    // Utiliser le mode_facturation_company s'il existe, sinon par défaut 'pour_compte_proprietaire'
    const mode = intervention.mode_facturation_company || 'pour_compte_proprietaire'
    return `Company:${mode}`
  } else if (type === 'Proprietaire') {
    return `Proprietaire:${intervention.entite_facturable_id}`
  } else if (type === 'LocationLocataire') {
    return `Locataire:${intervention.entite_facturable_id}`
  }
  
  return ''
}

export const InterventionStatuses = [
  { value: "demande_devis", label: "demande de devis engagée"}, 
  { value: 'attente_date', label: "en attente d’une date d’intervention"}, 
  { value: 'travaux_en_cours', label: "travaux en cours" },
  { value: 'travaux_finis', label: "travaux terminés"},
  { value: 'chantier_cloture', label: "chantier clôturé"},
  { value: 'sans_suite', label: "sans suite"}
]

export const InterventionStatusFilters = [
  { value: 'in_progress', label: "en cours", key: 'statut'},
  { value: "demande_devis", label: "demande de devis engagée", key: 'statut'}, 
  { value: 'attente_date', label: "en attente d’une date d’intervention", key: 'statut'}, 
  { value: 'travaux_en_cours', label: "travaux en cours", key: 'statut' },
  { value: 'travaux_finis', label: "travaux terminés", key: 'statut'},
  { value: 'chantier_cloture', label: "chantier clôturé", key: 'statut'},
  { value: 'sans_suite', label: "sans suite", key: 'statut'},
]

export const MaterielStatuses = [
  { value: 'charge_artisan', label: "à la charge de l’artisan"},
  { value: 'a_commander', label: "à commander"},
  { value: 'en_commande', label: "en commande"},
  { value: 'commande', label: 'commandé'},
  { value: 'disponible_magasin', label: 'disponible en retrait magasin'},
  { value: 'sur_chantier', label: 'disponible sur le chantier'}
]

export const FacturationStatuses = [
  { value: 'devis_en_cours', label: "devis en cours"},
  { value: 'devis_disponible', label: "devis disponible"},
  { value: 'devis_abandonne', label: "devis abandonné"},
  { value: 'devis_signe', label: "devis accepté et signé"}, 
  { value: 'facture_acompte_a_payer', label: "facture d’acompte à payer"},
  { value: 'facture_entiere_a_payer', label: "facture entière à payer"},
  { value: 'facture_cloture_a_payer', label: "facture de clôture à payer"},
  { value: 'facture_payee', label: "facture payée"}
]

export const FileFields = ['devis', 'facture_materiel', 'facture_acompte', 'facture_cloture', 'pv_livraison', 'photos']

const getStatusLabel = (statuses, key) => 
  statuses.find( s => s.value === key ).label

export const getInterventionStatusLabel = (status) => getStatusLabel(InterventionStatuses, status)
export const getMaterielStatusLabel = (status) => getStatusLabel(MaterielStatuses, status)
export const getFacturationStatusLabel = (status) => getStatusLabel(FacturationStatuses, status)

const URL = 'interventions'
const getUrl = (bien_id, id) => {
  let url = URL
  if(bien_id) {
    url = `biens/${bien_id}/${URL}`
  }
  if(id) {
    return `${url}/${id}`
  }
  return url
}

// États initiaux
const initialState = {
  items: [],
  current: null,
  inEdition: null,
  loadingStatus: {
    fetchList: false,
    fetchItem: false,
    create: false,
    update: false,
    delete: false,
  },
  requestStatus: {
    success: false,
    error: null,
    errors: {}
  },
  filters: {
    page: 1,
    limit: 10
  },
  pagination: {
    totalCount: 0,
    totalPages: 0,
    currentPage: 1
  },
  isCreatingNewPrestataire: false,
}

const sanitizeInterventionFormData = (data) => {
  // Étape 1: Nettoyer les données de base
  const sanitizedData = sanitizeFormData(data)
  
  // Étape 2: Supprimer les champs spécifiques non nécessaires
  delete sanitizedData.bien_id
  delete sanitizedData.bien
  delete sanitizedData.facture_adresse
  
  // Étape 3: Gérer le prestataire
  sanitizedData.prestataire_id = sanitizedData.prestataire?.id || null
  delete sanitizedData.prestataire

  // Étape 4: Traiter le champ entite_facturable
  if(sanitizedData.entite_facturable) {
    const [type, modeOrId] = sanitizedData.entite_facturable.split(':')

    sanitizedData.entite_facturable_type = type === 'Locataire' ? 'LocationLocataire' : type
    
    if (type === 'Company') {
      // Pour Company, l'ID est toujours 1 et utiliser getModeFacturationCompany pour le mode
      sanitizedData.entite_facturable_id = 1
      sanitizedData.mode_facturation_company = getModeFacturationCompany(sanitizedData.entite_facturable)
    } else {
      // Pour Proprietaire ou Locataire, le deuxième paramètre est l'ID
      sanitizedData.entite_facturable_id = modeOrId
    }
  }
  delete sanitizedData.entite_facturable

  // Étape 5: Vérifier si des fichiers sont présents
  // Utiliser hasFiles avec FileFields explicites et aussi la détection automatique
  const containsFiles = hasFiles(FileFields, sanitizedData) || hasFiles(sanitizedData)
  
  // Étape 6: Retourner soit un FormData, soit un objet simple
  if (containsFiles) {
    // Utiliser transformToFormData pour créer un FormData avec les champs de fichiers
    return transformToFormData(sanitizedData, 'intervention', FileFields)
  } else {
    // Si pas de fichiers, retourner un objet simple
    return { intervention: sanitizedData }
  }
}

// Thunks
export const fetchInterventions = createApiAsyncThunk(
  'interventions/fetchInterventions',
  async (params = {}, { api, dispatch, rejectWithValue }) => {
    try {
      await setTimeoutInDev()

      const bien_id = params.bien_id
      delete params.bien_id
      
      const response = await api
        .url(getUrl(bien_id))
        .query(params)
        .get()
      dispatch(addToPrestataireItems(response.interventions.map( i => i.prestataire)))
      return response
    } catch (error) {
      console.error(error)
      return rejectWithValue(error.message || 'Une erreur est survenue lors de la récupération des interventions')
    }
  }
)

export const createIntervention = createApiAsyncThunk(
  'interventions/createIntervention',
  async ({ bien_id, data}, { api, dispatch, rejectWithValue }) => {
    try {
      if(!bien_id) throw new Error("Une intervention doit être attachée à un bien")

      const response = await api
        .url(getUrl(bien_id))
        .post(sanitizeInterventionFormData(data))
      dispatch(addFlashMessage({type: 'success', text: "Intervention créée avec succès"}))
      if(response.prestataire_id) {
        dispatch(addToPrestataireItems([response.prestataire]))
      }
      return response
    } catch (error) {
      console.error(error)
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const updateIntervention = createApiAsyncThunk(
  'interventions/updateIntervention',
  async ({ bien_id, id, data }, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(getUrl(bien_id, id))
        .put(sanitizeInterventionFormData(data))
      dispatch(addFlashMessage({type: 'success', text: "Intervention mise à jour avec succès"}))
      if(response.prestataire_id) {
        dispatch(addToPrestataireItems([response.prestataire]))
      }
      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const deleteIntervention = createApiAsyncThunk(
  'interventions/deleteIntervention',
  async (intervention, { api, dispatch, rejectWithValue }) => {
    try {
      await setTimeoutInDev()
      const response = await api
        .url(getUrl(intervention.bien_id, intervention.id))
        .delete()
      dispatch(addFlashMessage({type: 'success', text: "Intervention a été supprimée avec succès"}))
      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

// Valeurs par défaut pour une intervention vide
export const emptyIntervention = {
  bien_id: null,
  prestataire_id: null,
  date_intervention: '',
  details: '',
  montant: '',
  statut: '',
  statut_materiel: '',
  statut_facturation: '',
  entite_facturable: '',
  materiel_a_fournir: '',
  info_rdv: '',
  display_to_proprietaire: true,
  display_to_locataire: true,
  display_to_acquereur: true
}

const sanitizesPayload = (action) => {
  const sanitized = Object.fromEntries(
    Object.entries(action.payload).map(([key, value]) =>
      [key, value === null ? (emptyIntervention[key] || "") : value]
    )
  )
  
  // Reconstituer la valeur entite_facturable pour le formulaire
  if (sanitized.entite_facturable_type) {
    sanitized.entite_facturable = buildEntiteFacturableValue(sanitized)
  }
  
  return sanitized
}

// Slice
const interventionsSlice = createSlice({
  name: 'interventions',
  initialState,
  reducers: {
    setEmptyCurrentIntervention: (state, action) => {
      const data = {
        ...emptyIntervention,
        ...action.payload
      }
      state.current = {
        ...data
      }
      state.requestStatus = initialState.requestStatus
    },
    setCurrentIntervention: (state, action) => {
      const sanitizedData = {
        ...emptyIntervention,
        ...sanitizesPayload(action)
      }
      state.current = {
        ...sanitizedData
      }
      state.requestStatus = initialState.requestStatus
    },
    resetCurrentIntervention: (state) => {
      state.current = initialState.current
      state.requestStatus = initialState.requestStatus
    },
    setInterventionInEdition: (state, action) => {
      state.inEdition = {
        ...emptyIntervention,
        ...sanitizesPayload(action)
      }
      state.requestStatus = initialState.requestStatus
    },
    resetInterventionInEdition: (state) => {
      state.inEdition = initialState.inEdition
      state.requestStatus = initialState.requestStatus
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    resetRequestStatus: (state) => {
      state.requestStatus = initialState.requestStatus
    },
    // Actions pour la gestion de la création du prestataire 
    startNewPrestataireCreation: (state) => {
      state.isCreatingNewPrestataire = true
    },
    finishNewPrestataireCreation: (state, action) => {
      const newPrestataire = {
        id: action.payload.id,
        nom_complet: action.payload.nom_complet || `${action.payload.prenom} ${action.payload.nom}`,
        email: action.payload.email,
        telephone: action.payload.telephone,
      }

      state.inEdition = {
        ...state.inEdition,
        prestataire: newPrestataire
      }

      state.isCreatingNewPrestataire = false
    },
    cancelNewPrestataireCreation: (state) => {
      state.isCreatingNewPrestataire = false
    }
  },
  extraReducers: (builder) => {
    // fetchInterventions
    builder.addCase(fetchInterventions.pending, (state) => {
      state.loadingStatus.fetchList = true
      state.requestStatus.error = null
    })
    builder.addCase(fetchInterventions.fulfilled, (state, action) => {
      state.items = action.payload.interventions.map( item => ({
        ...item,
        entite_facturable: buildEntiteFacturableValue(item)
      }))
      state.pagination = {
        totalCount: action.payload.total_count,
        totalPages: action.payload.total_pages,
        currentPage: action.payload.current_page
      }
      state.loadingStatus.fetchList = false
      state.requestStatus.success = true
    })
    builder.addCase(fetchInterventions.rejected, (state, action) => {
      state.loadingStatus.fetchList = false
      state.requestStatus.error = action.payload
    })

    // createIntervention
    builder.addCase(createIntervention.pending, (state) => {
      state.loadingStatus.create = true
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = false
    })
    builder.addCase(createIntervention.fulfilled, (state, action) => {
      state.items.unshift({
        ...action.payload,
        entite_facturable: buildEntiteFacturableValue(action.payload)
      })
      state.loadingStatus.create = false
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = true
    })
    builder.addCase(createIntervention.rejected, (state, action) => {
      state.loadingStatus.create = false
      state.requestStatus.errors = action.payload.errors ? action.payload.errors[0] : {}
      state.requestStatus.error = action.payload
      state.requestStatus.success = false
    })

    // updateIntervention
    builder.addCase(updateIntervention.pending, (state) => {
      state.loadingStatus.update = true
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = false
    })
    builder.addCase(updateIntervention.fulfilled, (state, action) => {
      const updated = action.payload
      const index = state.items.findIndex(item => item.id === updated.id)
      if (index !== -1) {
        state.items[index] = {
          ...updated,
          entite_facturable: buildEntiteFacturableValue(updated)
        }
      }
      state.loadingStatus.update = false
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = true
    })
    builder.addCase(updateIntervention.rejected, (state, action) => {
      state.loadingStatus.update = false
      state.requestStatus.error = action.payload
      state.requestStatus.errors = action.payload.errors ? action.payload.errors[0] : {}
      state.requestStatus.success = false
    })
 
    // Delete Intervention
    builder.addCase(deleteIntervention.pending, (state) => {
      state.loadingStatus.delete = true
      state.requestStatus.error = null
    })
    builder.addCase(deleteIntervention.fulfilled, (state, action) => {
      state.loadingStatus.delete = false
      const index = state.items.findIndex( item => item.id === action.payload.id)
      if (index !== -1) {
        state.items.splice(index, 1)
      }
      state.requestStatus.success = true
    })
    builder.addCase(deleteIntervention.rejected, (state, action) => {
      state.loadingStatus.delete = false
      state.requestStatus.error = action.payload
    })
  }
})

// Sélecteurs
export const selectInterventionsInProgress = createSelector(
  [ (state) => state.interventions.items, 
    // Ajouter un sélecteur qui retourne un nombre qui change à chaque mise à jour
    // (state) => state.interventions.items.map(i => i.id + i.statut).join(',')
  ],
  (interventions) => interventions.filter( i => i.statut !== "chantier_cloture")
)

export const {
  setEmptyCurrentIntervention,
  setCurrentIntervention,
  resetCurrentIntervention,
  setInterventionInEdition,
  resetInterventionInEdition,
  setFilters,
  resetRequestStatus,
  startNewPrestataireCreation,
  finishNewPrestataireCreation,
  cancelNewPrestataireCreation
} = interventionsSlice.actions

export default interventionsSlice.reducer